import { useEffect, useState } from "preact/compat";

interface VersionData {
  version: string;
  environment: string;
  buildTime: string;
  buildId: string;
}

const DEFAULT_VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

export const useVersionCheck = (
  checkInterval = DEFAULT_VERSION_CHECK_INTERVAL,
) => {
  const [currentBuildId, setCurrentBuildId] = useState<string | null>(null);
  const [showRefreshNotification, setShowRefreshNotification] = useState(false);
  const isLocalDev = import.meta.env.MODE === "localdev";

  const checkVersion = async (): Promise<VersionData | null> => {
    try {
      const response = await fetch("/version.json", {
        cache: "no-cache",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });

      if (response.ok) {
        const versionData: VersionData = await response.json();
        return versionData;
      }
    } catch (error) {
      console.warn("Version check failed:", error);
    }
    return null;
  };

  const forceRefresh = () => {
    if ("caches" in window) {
      caches.keys().then((cacheNames) => {
        cacheNames.forEach((cacheName) => {
          caches.delete(cacheName);
        });
      });
    }

    localStorage.setItem("lf_portal_build_id", currentBuildId || "");

    window.location.reload();
  };

  const checkForUpdates = async (isInitialCheck: boolean = false) => {
    if (isLocalDev) {
      return;
    }

    const versionData = await checkVersion();
    if (!versionData) return;

    const storedBuildId = localStorage.getItem("lf_portal_build_id");

    if (isInitialCheck) {
      localStorage.setItem("lf_portal_build_id", versionData.buildId);
      setCurrentBuildId(versionData.buildId);
      setShowRefreshNotification(false);
      return;
    }

    if (!storedBuildId) {
      localStorage.setItem("lf_portal_build_id", versionData.buildId);
      setCurrentBuildId(versionData.buildId);
      return;
    }

    if (storedBuildId !== versionData.buildId) {
      console.warn(
        `🔄 Build update detected: ${storedBuildId} -> ${versionData.buildId}`,
      );
      setShowRefreshNotification(true);
      setCurrentBuildId(versionData.buildId);
    } else {
      setShowRefreshNotification(false);
      setCurrentBuildId(versionData.buildId);
    }
  };

  useEffect(() => {
    if (isLocalDev) {
      return;
    }

    checkForUpdates(true);

    const interval = setInterval(() => checkForUpdates(false), checkInterval);

    return () => {
      clearInterval(interval);
    };
  }, [checkInterval, isLocalDev]);

  return {
    forceRefresh,
    showRefreshNotification,
  };
};
